import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type {
  LawQuestionnaireQuestion,
  ProductLawKey,
} from '@/shared/hooks/api/product'

export function lawQuestionnaireNamespace(law: ProductLawKey): string {
  return `lawQuestionnaire-${law}`
}

export function lawQuestionnaireQuestionLabelKey(questionKey: string): string {
  return `${questionKey}.label`
}

export function lawQuestionnaireQuestionHelperKey(questionKey: string): string {
  return `${questionKey}.helper`
}

export function shouldLocalizeLawQuestionnaireCopy(language: string): boolean {
  return language !== 'en-GB'
}

export function lawQuestionnaireTranslationNamespaces(
  law: ProductLawKey,
  language: string
): string[] {
  if (!shouldLocalizeLawQuestionnaireCopy(language)) {
    return ['common']
  }
  return ['common', lawQuestionnaireNamespace(law)]
}

export function useLawQuestionnaireTranslation(law: ProductLawKey) {
  const { i18n } = useTranslation('common')
  const namespaces = useMemo(
    () => lawQuestionnaireTranslationNamespaces(law, i18n.language),
    [law, i18n.language]
  )
  const { t } = useTranslation(namespaces)
  return { t, i18n }
}

export function localizeLawQuestionnaireQuestionLabel(
  translate: TFunction,
  law: ProductLawKey,
  question: Pick<LawQuestionnaireQuestion, 'questionKey' | 'label'>,
  language: string
): string {
  if (!shouldLocalizeLawQuestionnaireCopy(language)) {
    return question.label
  }
  return translate(lawQuestionnaireQuestionLabelKey(question.questionKey), {
    ns: lawQuestionnaireNamespace(law),
    defaultValue: question.label,
  })
}

export function localizeLawQuestionnaireQuestionHelper(
  translate: TFunction,
  law: ProductLawKey,
  question: Pick<LawQuestionnaireQuestion, 'questionKey' | 'helper'>,
  language: string
): string | null {
  if (question.helper == null || question.helper.length === 0) {
    return null
  }
  if (!shouldLocalizeLawQuestionnaireCopy(language)) {
    return question.helper
  }
  return translate(lawQuestionnaireQuestionHelperKey(question.questionKey), {
    ns: lawQuestionnaireNamespace(law),
    defaultValue: question.helper,
  })
}

export function localizeLawQuestionnaireQuestion(
  translate: TFunction,
  law: ProductLawKey,
  question: LawQuestionnaireQuestion,
  language: string
): LawQuestionnaireQuestion {
  return {
    ...question,
    label: localizeLawQuestionnaireQuestionLabel(
      translate,
      law,
      question,
      language
    ),
    helper: localizeLawQuestionnaireQuestionHelper(
      translate,
      law,
      question,
      language
    ),
  }
}

export function localizeLawQuestionnaireQuestions(
  translate: TFunction,
  law: ProductLawKey,
  questions: LawQuestionnaireQuestion[],
  language: string
): LawQuestionnaireQuestion[] {
  return questions.map((question) =>
    localizeLawQuestionnaireQuestion(translate, law, question, language)
  )
}
